'use strict';

const accessorials = require('../accessorials');
const { getPropValue } = require('../utilities');

var _parent = {};
var _api = {};

var stripe = {
  init: p => {
    _parent = p;
    _api = require('stripe')(p.config.stripe_key);
  },

  /**
   * Approve the authorization by id
   *
   * @param {String} id - The unique authorization identifier from Stripe
   * @param {Number} amount - If the authorization’s pending_request.is_amount_controllable property is true, you may provide this value to control how much to hold for the authorization.
   * @returns {Boolean} Return true if successfully authorized else throws error
   */
  approve: async (id, amount, timer) => {
    //Try up to 3 times to send the approval since sometimes the calls fail
    let res;
    try {
      res = await _api.issuing.authorizations.approve(id, { amount: amount * 100 }, timer);
      console.log(`Approved swipe: ⏰${Date.now() - timer}ms`);
    } catch (error) {
      try {
        res = await _api.issuing.authorizations.approve(id, { amount: amount * 100 });
      } catch (error) {
        try {
          res = await _api.issuing.authorizations.approve(id, { amount: amount * 100 });
        } catch (error) {
          let errorMessage = error && error.raw && error.raw.message ? error.raw.message : 'no error message';
          console.log(`stripe.approve error: ⏰${Date.now() - timer}ms`, errorMessage);
          throw error;
        }
      }
    }

    return true;
  },

  /**
   * Check if the passed payload and headers constitute a valid Stripe webhook
   *
   * @param {Object} body - An HTTP request payload object
   * @param {Array} headers - An HTTP request headers array
   * @returns {StripeEvent} The stripe event object if its a valid request else throws error
   */
  constructEvent: (body, headers) => {
    //Parse and check the signature of the request payload to ensure it is a valid stripe
    // event from the stripe servers. If it is not, we should not process it.
    return _api.webhooks.constructEvent(body, headers['stripe-signature'], _parent.config.stripe_webhook_secret);
  },

  /**
   * @typedef {Object} IssuingAuthorizationResponse
   * @property {Boolean} approved - True if the authorization was approved
   * @property {Boolean} amount - The max amount that was approved
   * @property {String} message - Any message about the authorization attempt
   */

  /**
   * Parse the stripe event, identify an applicable accessorial authorization
   * and check for approval. If it should be approved call the stripe approval
   * API and if not, then decline the authorization.
   *
   * @param {Object} stripeEvent - The [Stripe Authorization Request Object]{@link https://stripe.com/docs/api/issuing/authorizations/object} passed by the Stripe issuing authorization request webhook
   * @returns {IssuingAuthorizationResponse} An object with boolean success and optional message
   */
  processIssuingAuthorizationRequest: async stripeEvent => {
    //Destructure all required properties from the stripeEvent object
    const {
      data: {
        object: {
          id: vendorAuthId,
          card: { id: cardId, last4 } = {},
          merchant_data: {
            category: merchantCategory,
            name: merchantName,
            city: merchantCity,
            state: merchantState,
            postal_code: merchantPostalcode,
          } = {},
          pending_request: { amount: pendingChargeAmount } = {},
        } = {},
      } = {},
    } = stripeEvent || {};
    const timer = Date.now();

    //Get cardHolderId to look up driverId
    const cardholder = getPropValue(stripeEvent, 'data.object.card.cardholder');
    if (!cardholder) return new Error('No driver data found in stripe data object');
    //Get the driver from the swiped card
    let driverId = cardholder.metadata ? Number(cardholder.metadata.driver_id) : null;
    if (!driverId || typeof driverId !== "number") return new Error('No driver data found in cardholder metadata: ', cardholder);

    const { individual = {} } = cardholder;
    let driver = {
      id: driverId,
      first_name: individual.first_name || '',
      last_name: individual.last_name || '',
    };
    console.log(` ⏰ I) Got driver id from stripe. function has taken ${Date.now() - timer} milliseconds`);

    //We have a valid authorization request for a Issuing Card Transaction
    let requestedChargeAmount = pendingChargeAmount / 100; //Comes over without decimal

    console.log(
      `⛽️ Fuel card swiped by driver ${driver.id} (${driver.first_name} ${driver.last_name}) requesting the amount of $${requestedChargeAmount}`
    );

    // sample response { approved: true, amount: 8.50, message: null }
    const approval = await _parent.authorizations.isApproved(
      driver.id,
      {
        vendorAuthId: vendorAuthId,
        cardId: cardId,
        last4: last4,
        merchantCategory: merchantCategory,
        merchantName: merchantName,
        merchantCity: merchantCity,
        merchantState: merchantState,
        merchantPostalcode: merchantPostalcode,
        driverName: `${driver.first_name} ${driver.last_name}`,
        amount: requestedChargeAmount,
      },
      timer
    );
    console.log(` ⏰ II) Checked if swipe should be approved. function has taken ${Date.now() - timer} milliseconds`);
    const { approved, amount, message, accessorial = {} } = approval;
    const { authorization = {} } = accessorial;

    try {
      if (approved) {
        console.log(`💸 The authorization should be approved! The max allowed charge amount is $${amount}`);

        //If we can safely update the accessorial to approved, then we
        //reply to Stripe with an approval
        const approveResponse = await stripe.approve(vendorAuthId, amount, timer);
        console.log('Stripe Approval: ', approveResponse);

        const authResponse = await _parent.authorizations.approve({
          accessorialId: accessorial.id,
          authorizationId: authorization.id,
          vendorAuthId,
          cardId,
          last4,
          merchantName,
          merchantCity,
          merchantState,
          merchantPostalcode,
          driverId: driver.id,
          driverName: `${driver.first_name} ${driver.last_name}`,
          amount,
        });
        console.log(`Approving Accessorial #${accessorial.id}:`, authResponse);
        console.log(` ⏰ III) Finished approving. function has taken ${Date.now() - timer} milliseconds`);
      } else {
        console.log(`💸 The authorization should NOT be approved! ${message}`);
      }
    } catch (error) {
      //If we made it here then the Stripe API call to approve the transaction
      // failed and we need to set it up for another retry in the future.
      let errorMessage = error && error.raw && error.raw.message ? error.raw.message : error;
      console.log('Error Catch:', errorMessage);

      await _parent.authorizations.declineWithRetry({
        accessorialId: accessorial.id,
        authorizationId: authorization.id,
        vendorAuthId,
        cardId,
        last4,
        merchantName,
        merchantCity,
        merchantState,
        merchantPostalcode,
        driverId: driver.id,
        driverName: `${driver.first_name} ${driver.last_name}`,
        reason: error.message,
        authAttempts: authorization.auth_attempts,
        existingNotes: authorization.notes,
      });
      return { approved: false, amount, message: error.message };
    }

    return { approved, amount, message, driver, accessorial };
  },
};

module.exports = stripe;
